import { createFileRoute } from '@tanstack/react-router'
import { cx } from '@/lib/clases'
import { formatearMonto } from '@/lib/monto'
import { formatearFecha, formatearFechaHora } from '@/lib/fecha'
import { formatearRNC } from '@/lib/rnc'
import { Boton } from '@/ui/Boton'
import { Banner } from '@/ui/Banner'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { Esqueleto } from '@/ui/Esqueleto'
import { InsigniaDiscreta, InsigniaEstado, PuntoEstado } from '@/ui/InsigniaEstado'
import { ENCF } from '@/ui/Numero'
import { nombreTipoECF } from '@/dominio/documentos/tipos'
import { tonoDeVigencia, type Certificado } from '@/dominio/certificado/tipos'
import { useCertificado } from '@/dominio/certificado/useCertificado'
import { useHuecos, useSecuencias } from '@/dominio/secuencias/useSecuencias'
import type { RangoConTipo } from '@/dominio/secuencias/tipos'
import { BarraConsumo, Dato, EncabezadoPantalla, Tarjeta } from './-pantalla'

/**
 * Configuración (E12, E18, E32).
 *
 * Entra lo que el backend puede sostener hoy: **secuencias por rango
 * autorizado**, **huecos** y **certificado**. Lo demás —usuarios, plantilla de
 * factura, plan— dice honestamente qué falta, en vez de enseñar un formulario
 * que no guarda nada. Un botón que no hace nada es peor que su ausencia: enseña
 * al usuario a desconfiar del resto.
 */
export const Route = createFileRoute('/configuracion')({
  component: PantallaConfiguracion,
})

function PantallaConfiguracion() {
  const { consulta, rangos } = useSecuencias()
  const { consulta: consultaHuecos, huecos, total: totalHuecos } = useHuecos()
  const certificado = useCertificado()

  return (
    <div className="flex max-w-[1000px] flex-col gap-6">
      <EncabezadoPantalla titulo="Configuración" />

      {/* Certificado ---------------------------------------------------- */}
      <Tarjeta titulo="Certificado de firma">
        {certificado.isLoading || certificado.data === undefined ? (
          <Esqueleto className="h-24 w-full" />
        ) : (
          <FichaDelCertificado certificado={certificado.data} />
        )}
      </Tarjeta>

      {/* Secuencias ----------------------------------------------------- */}
      <Tarjeta
        titulo="Secuencias autorizadas"
        relleno={false}
        acciones={
          consulta.data !== undefined ? (
            <span className="text-etiqueta text-gris-600">
              aviso al {consulta.data.umbralConsumoPorCiento}% consumido y a{' '}
              {consulta.data.umbralDiasVencimiento} días del vencimiento
            </span>
          ) : null
        }
      >
        {consulta.isLoading ? (
          <div className="p-4">
            <Esqueleto className="h-24 w-full" />
          </div>
        ) : rangos.length === 0 ? (
          <EstadoVacio variante="primera-vez" mensaje="Todavía no hay rangos autorizados cargados." />
        ) : (
          <table className="w-full table-fixed border-collapse text-cuerpo" data-densidad="normal">
            <thead className="bg-gris-50">
              <tr className="border-b border-gris-300">
                <th scope="col" className="h-[var(--alto-fila)] w-[150px] px-2 text-etiqueta font-semibold text-gris-600">
                  Tipo
                </th>
                <th scope="col" className="h-[var(--alto-fila)] w-[180px] px-2 text-right text-etiqueta font-semibold text-gris-600">
                  Rango
                </th>
                <th scope="col" className="h-[var(--alto-fila)] w-[110px] px-2 text-right text-etiqueta font-semibold text-gris-600">
                  Usados
                </th>
                <th scope="col" className="h-[var(--alto-fila)] w-[120px] px-2 text-right text-etiqueta font-semibold text-gris-600">
                  Disponibles
                </th>
                <th scope="col" className="h-[var(--alto-fila)] px-2 text-etiqueta font-semibold text-gris-600">
                  Consumo
                </th>
                <th scope="col" className="h-[var(--alto-fila)] w-[120px] px-2 text-right text-etiqueta font-semibold text-gris-600">
                  Vence
                </th>
                <th scope="col" className="h-[var(--alto-fila)] w-[150px] px-2 text-etiqueta font-semibold text-gris-600">
                  Estado
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Una fila **por rango autorizado**, no por tipo. */}
              {rangos.map((rango) => (
                <FilaDeRango key={rango.id} rango={rango} />
              ))}
            </tbody>
          </table>
        )}
      </Tarjeta>

      {/* Huecos --------------------------------------------------------- */}
      <Tarjeta
        titulo="Secuencias quemadas"
        relleno={false}
        acciones={
          <span className="text-etiqueta text-gris-600">
            {totalHuecos > 0 ? `${formatearMonto(totalHuecos, { decimales: 0 })} · ` : ''}visible a propósito
          </span>
        }
      >
        {consultaHuecos.isLoading ? (
          <div className="p-4">
            <Esqueleto className="h-16 w-full" />
          </div>
        ) : huecos.length === 0 ? (
          <EstadoVacio variante="resuelto" mensaje="Ningún hueco registrado." />
        ) : (
          <>
            <p className="border-b border-gris-200 px-4 py-2 text-etiqueta text-gris-600">
              El criterio es sin duplicados, monótono creciente y{' '}
              <strong className="font-medium text-gris-900">todo hueco con su causa</strong>. Los huecos
              ocurren; lo que importa es que cada uno tenga explicación.
            </p>
            <ul className="flex flex-col">
              {huecos.map((hueco) => (
                <li
                  key={hueco.id}
                  className="flex flex-wrap items-baseline gap-3 border-b border-gris-200 px-4 py-2 last:border-b-0"
                >
                  <ENCF valor={hueco.eNCF} />
                  <span className="flex-1 text-cuerpo text-gris-700">{hueco.causa}</span>
                  {/* De dónde salió el hueco: un rechazo de DGII y una anotación
                      del libro se explican distinto ante una fiscalización. */}
                  <InsigniaDiscreta>
                    {hueco.origen === 'rechazo_dgii' ? 'rechazo de DGII' : 'libro'}
                  </InsigniaDiscreta>
                  {hueco.resueltaEl !== null ? (
                    <InsigniaEstado tono="verde">resuelto</InsigniaEstado>
                  ) : null}
                  <span className="numero text-gris-600">{formatearFechaHora(hueco.momento)}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </Tarjeta>

      {/* Lo que falta --------------------------------------------------- */}
      <Tarjeta titulo="Lo que todavía no se puede configurar aquí" relleno={false}>
        <ul className="flex flex-col">
          <PendienteDeBackend
            titulo="Usuarios y permisos"
            falta="Faltan los extremos de usuarios. El rol se relee de la base en cada petición, así que la pantalla no puede fingirlo con el testigo."
          />
          <PendienteDeBackend
            titulo="Plantilla de la representación impresa"
            falta="Falta la generación del PDF en el servidor. La vista previa en vivo no sirve de nada si el PDF real lo compone otro código."
          />
          <PendienteDeBackend
            titulo="Carga y rotación del certificado"
            falta="Falta POST /interna/certificados: hoy el certificado solo se consulta. La validación de la cadena y del SN es del servidor; hacerla solo en el navegador daría una falsa sensación de comprobado."
          />
          <PendienteDeBackend
            titulo="Anulación de rangos y corte de secuencias"
            falta="Faltan los extremos de gestión de secuencias. Lo que hay es la consulta, así que la pantalla enseña el estado y no ofrece acciones que no existen."
          />
          <PendienteDeBackend
            titulo="Plan y facturación"
            falta="El cobro recurrente está pospuesto fuera de la v1 y durante los primeros clientes se factura a mano."
          />
        </ul>
      </Tarjeta>
    </div>
  )
}

/**
 * La tarjeta del certificado.
 *
 * **Sin certificado no se enseña una ficha vacía, se enseña el bloqueo.** Antes
 * se pintaban cinco campos en blanco y unos días restantes que no existían, y
 * un contribuyente que no puede firmar salía de esta pantalla creyendo que
 * estaba todo bien.
 */
function FichaDelCertificado({ certificado }: { certificado: Certificado }) {
  const tono = tonoDeVigencia(certificado)

  if (!certificado.configurado) {
    return (
      <div className="flex flex-col gap-3">
        <Banner tono="rojo" titulo="No hay certificado de firma. No puedes emitir.">
          {/* El texto lo redacta el servidor: es el mismo que alimenta el panel
              de «requieren atención» del tablero, y decirlo distinto en dos
              sitios es como se pierde la confianza en los dos. */}
          {certificado.avisos.map((aviso) => (
            <p key={aviso.codigo}>{aviso.mensaje}</p>
          ))}
        </Banner>
        <Boton variante="secundario" disabled>
          Cargar certificado
        </Boton>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-6">
        <dl className="min-w-[280px] flex-1">
          {certificado.titular !== null ? (
            <>
              {/*
                El SN del certificado es el documento del FIRMANTE, no el RNC
                del tenant. Son dos comprobaciones distintas y reducirlas a una
                rechaza certificados personales válidos.
              */}
              <Dato etiqueta="SN del certificado">
                <span className="mono">{certificado.titular.sn}</span>
              </Dato>
              <Dato etiqueta="Documento del firmante">
                <span className="numero">{formatearRNC(certificado.titular.documentoFirmante)}</span>
              </Dato>
              <Dato etiqueta="RNC delegado">
                <span className="flex items-center justify-end gap-2">
                  <span className="numero">{formatearRNC(certificado.titular.rncDelegado)}</span>
                  {certificado.titular.coincideConElTenant ? null : (
                    <InsigniaEstado tono="rojo">no es el de este contribuyente</InsigniaEstado>
                  )}
                </span>
              </Dato>
            </>
          ) : null}
          {certificado.vigenteDesde !== null && certificado.venceEl !== null ? (
            <Dato etiqueta="Vigencia">
              <span className="numero">
                {formatearFecha(certificado.vigenteDesde)} – {formatearFecha(certificado.venceEl)}
              </span>
            </Dato>
          ) : null}
          {certificado.huellaSha256 !== null ? (
            <Dato etiqueta="Huella SHA-256">
              <span className="mono break-all text-etiqueta">{certificado.huellaSha256}</span>
            </Dato>
          ) : null}
          {certificado.estado !== null ? (
            <Dato etiqueta="Estado">
              <span className="capitalize">{certificado.estado}</span>
            </Dato>
          ) : null}
        </dl>

        <div className="flex flex-col items-end gap-2">
          <span className="text-etiqueta text-gris-600">Días restantes</span>
          <span
            className={cx(
              'numero text-[36px] leading-10',
              tono === 'rojo' ? 'text-rojo' : tono === 'ambar' ? 'text-ambar' : 'text-verde',
            )}
          >
            {certificado.diasParaVencer ?? '—'}
          </span>
          <Boton variante="secundario" disabled>
            Rotar certificado
          </Boton>
          {/* El anterior se conserva para verificar XML históricos. */}
          <p className="max-w-[220px] text-right text-etiqueta text-gris-600">
            Al rotar, el anterior se conserva para poder verificar los XML ya firmados.
          </p>
        </div>
      </div>

      {/* El umbral del ámbar lo decide el servidor, que cuenta los días con el
          calendario dominicano. Aquí solo se pinta lo que ya venía resuelto. */}
      {certificado.bloqueaLaEmision || certificado.porVencer ? (
        <Banner
          tono={certificado.bloqueaLaEmision ? 'rojo' : 'ambar'}
          className="mt-4"
          titulo={
            certificado.vencido && certificado.venceEl !== null
              ? `No puedes facturar: tu certificado venció el ${formatearFecha(certificado.venceEl)}`
              : certificado.bloqueaLaEmision
                ? 'No puedes facturar con este certificado'
                : `El certificado vence en ${certificado.diasParaVencer ?? '—'} días`
          }
        >
          {certificado.avisos.length > 0
            ? certificado.avisos.map((aviso) => <p key={aviso.codigo}>{aviso.mensaje}</p>)
            : 'La emisión del certificado por una entidad autorizada por INDOTEL tarda. Empieza la renovación ahora.'}
        </Banner>
      ) : null}
    </>
  )
}

function FilaDeRango({ rango }: { rango: RangoConTipo }) {
  const anulado = rango.estado === 'anulado'

  return (
    <tr className="h-[var(--alto-fila)] border-b border-gris-200 last:border-b-0">
      <td className="px-2">
        <span className="flex items-baseline gap-2">
          <span className="mono text-gris-600">{rango.tipo}</span>
          <span className="truncate text-gris-900" title={rango.nombreDelTipo}>
            {nombreTipoECF(rango.tipo)}
          </span>
        </span>
      </td>
      <td className="numero px-2 text-gris-700">
        {anulado ? '—' : `${formatearMonto(rango.desde, { decimales: 0 })}–${formatearMonto(rango.hasta, { decimales: 0 })}`}
      </td>
      <td className="numero px-2 text-gris-700">
        {anulado ? '—' : formatearMonto(rango.consumidos, { decimales: 0 })}
      </td>
      <td className="numero px-2 text-gris-700">
        {anulado ? '—' : formatearMonto(rango.disponibles, { decimales: 0 })}
      </td>
      <td className="px-2">
        {anulado ? (
          <span className="text-etiqueta text-gris-400">—</span>
        ) : (
          <span className="flex items-center gap-2">
            <BarraConsumo porcentaje={rango.porcentajeConsumido} />
            <span className="numero shrink-0 text-gris-600">{rango.porcentajeConsumido}%</span>
          </span>
        )}
      </td>
      <td className="numero px-2 text-gris-700">
        {/*
          El tipo 32 y el 34 NO vencen. Enseñar una fecha inventada ahí es un
          defecto, no un detalle: acaba impresa en la representación.
        */}
        {rango.venceEl === null ? (
          <span className="text-gris-400" title="Este tipo no lleva fecha de vencimiento.">
            no vence
          </span>
        ) : (
          formatearFecha(rango.venceEl)
        )}
      </td>
      <td className="px-2">
        <span className="flex items-center gap-2">
          {/* `utilizable` y `vencido` los decide el servidor. Aquí solo quedan
              los cortes de color, que son presentación. */}
          <PuntoEstado
            tono={
              anulado
                ? 'neutral'
                : rango.vencido || !rango.utilizable
                  ? 'rojo'
                  : rango.porcentajeConsumido >= 95
                    ? 'rojo'
                    : rango.porcentajeConsumido >= 80
                      ? 'ambar'
                      : rango.estado === 'reserva'
                        ? 'azul'
                        : 'verde'
            }
          />
          <span className="capitalize text-gris-900">{rango.estado}</span>
        </span>
      </td>
    </tr>
  )
}

/** Estado vacío honesto: qué falta y por qué, en vez de un formulario muerto. */
function PendienteDeBackend({ titulo, falta }: { titulo: string; falta: string }) {
  return (
    <li className="flex flex-col gap-px border-b border-gris-200 px-4 py-2 last:border-b-0">
      <span className="text-cuerpo font-medium text-gris-900">{titulo}</span>
      <span className="text-etiqueta text-gris-600">{falta}</span>
    </li>
  )
}
